import { useEffect, useRef } from 'react'
import toast from 'react-hot-toast'

import { useAuth } from 'hooks/useAuth'
import { googleExchangeToken } from 'services/auth'
import { trueErrorMessage } from 'utils/error'
import { GoogleCredentialResponse, loadGoogleIdentity } from 'utils/google-identity'

const UNAVAILABLE_MESSAGE = 'Google sign-in is unavailable right now. Please try again or use another sign-in method.'

/** Google's own "Sign in with Google" button; its ID token is exchanged for a session, with no popup page of ours. */
export function GoogleSignInButton({ clientId, isModal }: Readonly<{ clientId: string; isModal?: boolean }>) {
	const { setToken, setRefreshToken, setAuthInfo, setIsSignedUp } = useAuth()
	const container = useRef<HTMLDivElement>(null)

	const onCredential = async ({ credential }: GoogleCredentialResponse) => {
		try {
			if (!credential) throw new Error('No credential')
			const result: any = await googleExchangeToken(credential)
			if (!result.token) throw new Error('No session')

			setToken(result.token)
			setRefreshToken(result.refreshToken)
			setAuthInfo(result)
			setIsSignedUp(true)

			if (isModal) {
				window.location.reload()
			}
		} catch (error: any) {
			toast.error(error?.data ? trueErrorMessage(error.data) : UNAVAILABLE_MESSAGE)
		}
	}

	// Google keeps the callback it was initialised with, so hand it the latest one through a ref.
	const latest = useRef(onCredential)
	latest.current = onCredential

	useEffect(() => {
		const element = container.current
		let cancelled = false

		loadGoogleIdentity()
			.then((google) => {
				if (cancelled || !element) return
				google.accounts.id.initialize({ client_id: clientId, callback: (response) => latest.current(response) })
				google.accounts.id.renderButton(element, { type: 'icon', shape: 'circle', theme: 'outline', size: 'large' })
			})
			.catch(() => toast.error(UNAVAILABLE_MESSAGE))

		return () => {
			cancelled = true
			if (element) element.innerHTML = ''
		}
	}, [clientId])

	// Google sizes its button for content-box; Tailwind's border-box reset collapses the logo to 0x0 without this.
	return <div ref={container} className="w-11 h-11 flex justify-center items-center [&_*]:box-content" />
}
